import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, X, Star, Clock, UserCheck, ArrowRight, Target,
  ShieldCheck, Sparkles, Award, ChevronDown, ChevronRight,
} from "lucide-react";
import { listApps } from "@/lib/api";
import { categoryColor } from "@/constants/categoryColors";
import { BEST_HORSE_APPS_CONTENT } from "@/constants/bestHorseAppsContent";
import { useLang } from "@/context/LanguageContext";
import OgPreviewTooltip from "@/components/OgPreviewTooltip";

const BASE_URL = "https://horseappcompare.com";

const useDocumentMeta = ({ title, description, url, alternates }) => {
  useEffect(() => {
    document.title = title;
    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "article", "property");
    setMeta("og:url", url, "property");

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Remove any previous hreflangs we added and re-add
    document.querySelectorAll("link[data-hreflang]").forEach((n) => n.remove());
    (alternates || []).forEach((alt) => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", alt.lang);
      l.setAttribute("href", alt.href);
      l.setAttribute("data-hreflang", "true");
      document.head.appendChild(l);
    });

    return () => {
      document.querySelectorAll("link[data-hreflang]").forEach((n) => n.remove());
    };
  }, [title, description, url, JSON.stringify(alternates)]);
};

const AppPickRow = ({ app, reason, index, monthlyLabel, freeLabel }) => {
  if (!app) return null;
  return (
    <OgPreviewTooltip slug={app.slug} className="block">
      <Link
        to={`/apps/${app.slug}`}
        className="flex items-start gap-4 p-4 border border-[#E2E8F0] rounded-xl hover:border-[#2563EB] hover:bg-[#EFF6FF]/40 transition-colors"
      >
      {index !== undefined && (
        <div className="font-mono text-2xl text-[#94A3B8] w-10 shrink-0">
          0{index + 1}
        </div>
      )}
      <span
        className="w-12 h-12 shrink-0 flex items-center justify-center text-white font-bold text-lg rounded-lg"
        style={{ backgroundColor: app.color || "#2563EB" }}
      >
        {app.name.charAt(0)}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-bold text-lg text-[#0F172A]">{app.name}</h4>
          <span className="inline-flex items-center gap-1 text-xs text-[#64748B]">
            <Star size={12} className="fill-[#2563EB] text-[#2563EB]" />
            {app.rating?.toFixed(1)}
          </span>
          <span className="text-xs text-[#94A3B8] font-mono">
            {app.pricing?.free_tier && app.pricing?.monthly_usd === 0
              ? freeLabel
              : `$${app.pricing?.monthly_usd}${monthlyLabel}`}
          </span>
        </div>
        <p className="text-sm text-[#64748B] mt-1 leading-relaxed">
          {reason || app.tagline}
        </p>
      </div>
      <ChevronRight size={18} className="text-[#94A3B8] mt-2 shrink-0" />
      </Link>
    </OgPreviewTooltip>
  );
};

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border border-[#E2E8F0] rounded-xl bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-4 p-5 text-left"
        aria-expanded={open}
      >
        <span className="font-bold text-[#0F172A]">{q}</span>
        <ChevronDown
          size={20}
          className={`text-[#64748B] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-5 pb-5 text-[#64748B] leading-relaxed">{a}</div>}
    </div>
  );
};

const BestHorseAppsPage = ({ language = "en" }) => {
  const { setLang } = useLang();
  const c = BEST_HORSE_APPS_CONTENT[language];
  const other = BEST_HORSE_APPS_CONTENT[language === "en" ? "nl" : "en"];
  const [apps, setApps] = useState([]);

  useEffect(() => {
    setLang(language);
  }, [language, setLang]);

  useEffect(() => {
    listApps({ limit: 100 }).then(setApps).catch(() => setApps([]));
  }, []);

  const url = `${BASE_URL}${c.urlPath}`;
  const alternates = [
    { lang: "en", href: `${BASE_URL}/best-horse-apps` },
    { lang: "nl", href: `${BASE_URL}/beste-paardenapps` },
    { lang: "x-default", href: `${BASE_URL}/best-horse-apps` },
  ];

  useDocumentMeta({ title: c.title, description: c.description, url, alternates });

  const bySlug = (slug) => apps.find((a) => a.slug === slug);
  const topTen = [...apps].sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 10);

  // ---- Structured data ---------------------------------------------------
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: c.breadcrumbHome, item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: c.breadcrumbHere, item: url },
    ],
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    numberOfItems: topTen.length,
    itemListElement: topTen.map((a, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "SoftwareApplication",
        name: a.name,
        operatingSystem: [
          a.platforms?.ios && "iOS",
          a.platforms?.android && "Android",
          a.platforms?.web && "Web",
        ].filter(Boolean).join(", "),
        applicationCategory: "LifestyleApplication",
        offers: { "@type": "Offer", price: a.pricing?.monthly_usd || 0, priceCurrency: "USD" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: a.rating, reviewCount: a.review_count || 1 },
        url: `${BASE_URL}/apps/${a.slug}`,
      },
    })),
  };

  return (
    <main>
      {/* Hero */}
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-16 md:py-24 max-w-5xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-6">
            <Link to="/" className="hover:text-[#2563EB]">{c.breadcrumbHome}</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{c.breadcrumbHere}</span>
          </nav>

          <div className="overline mb-4 text-[#2563EB]">
            <Award size={12} className="inline mr-2" />
            {c.eyebrow}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-[#0F172A] leading-[1.05] mb-6">
            {c.h1}
          </h1>
          <p className="text-lg md:text-xl text-[#64748B] leading-relaxed max-w-3xl mb-8">
            {c.heroLede}
          </p>

          <div className="flex flex-wrap items-center gap-6 text-sm text-[#64748B]">
            <span className="inline-flex items-center gap-2">
              <Clock size={16} className="text-[#2563EB]" /> {c.updatedLabel} {c.updatedDate}
            </span>
            <span className="inline-flex items-center gap-2">
              <UserCheck size={16} className="text-[#2563EB]" /> {c.authorLabel} {c.author}
            </span>
          </div>

          <div className="mt-6 text-xs">
            <Link to={other.urlPath} className="text-[#2563EB] hover:text-[#1D4ED8]" data-testid={`hreflang-${other.lang}`}>
              {other.lang === "nl" ? "🇳🇱 Lees in het Nederlands" : "🇬🇧 Read in English"}
            </Link>
          </div>
        </div>
      </section>

      {/* Quick comparison */}
      <section className="hac-container py-16 md:py-20">
        <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-3">
          {c.quickCompareTitle}
        </h2>
        <p className="text-[#64748B] max-w-3xl mb-8">{c.quickCompareLede}</p>

        <div className="scroll-x bg-white border border-[#E2E8F0] rounded-2xl">
          <table className="w-full min-w-max text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F1F5F9]">
                <th className="p-4">{c.cols.app}</th>
                <th className="p-4">{c.cols.rating}</th>
                <th className="p-4">{c.cols.price}</th>
                <th className="p-4 text-center">{c.cols.ai}</th>
                <th className="p-4 text-center">{c.cols.gps}</th>
                <th className="p-4 text-center">{c.cols.health}</th>
                <th className="p-4 text-center">{c.cols.stable}</th>
                <th className="p-4 text-center">{c.cols.multi}</th>
                <th className="p-4">{c.cols.platforms}</th>
              </tr>
            </thead>
            <tbody>
              {topTen.map((a) => {
                const has = (f) => (a.features || []).includes(f);
                const hasAny = (list) => list.some(has);
                const platforms = [
                  a.platforms?.ios && "iOS",
                  a.platforms?.android && "Android",
                  a.platforms?.web && "Web",
                ].filter(Boolean).join(" · ");
                const yesIcon = <Check size={16} className="text-[#2563EB] inline" />;
                const noIcon = <X size={14} className="text-[#CBD5E1] inline" />;
                return (
                  <tr key={a.slug} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="p-4">
                      <OgPreviewTooltip slug={a.slug} className="inline-block">
                        <Link to={`/apps/${a.slug}`} className="flex items-center gap-3 hover:text-[#2563EB]">
                          <span className="w-8 h-8 flex items-center justify-center text-white font-bold rounded-md"
                            style={{ backgroundColor: a.color || "#2563EB" }}>
                            {a.name.charAt(0)}
                          </span>
                          <span className="font-semibold text-[#0F172A]">{a.name}</span>
                        </Link>
                      </OgPreviewTooltip>
                    </td>
                    <td className="p-4 font-mono text-[#0F172A]">{a.rating?.toFixed(1)} ★</td>
                    <td className="p-4 font-mono text-[#64748B]">
                      {a.pricing?.free_tier && a.pricing?.monthly_usd === 0
                        ? c.free
                        : `$${a.pricing?.monthly_usd}${c.monthly}`}
                    </td>
                    <td className="p-4 text-center">
                      {hasAny(["ai_coach", "ai_competition_analysis", "ai_nutrition", "ai_injury", "ai_breeding", "ai_management"]) ? yesIcon : noIcon}
                    </td>
                    <td className="p-4 text-center">{has("gps_tracking") ? yesIcon : noIcon}</td>
                    <td className="p-4 text-center">{hasAny(["vet_records", "medication", "vaccinations"]) ? yesIcon : noIcon}</td>
                    <td className="p-4 text-center">{hasAny(["box_management", "boarding_clients", "billing"]) ? yesIcon : noIcon}</td>
                    <td className="p-4 text-center">{hasAny(["staff", "coach_support"]) ? yesIcon : noIcon}</td>
                    <td className="p-4 text-xs text-[#64748B]">{platforms}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* What is a horse app */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0] py-16 md:py-20">
        <div className="hac-container max-w-4xl">
          <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-6">
            {c.whatIsTitle}
          </h2>
          <div className="space-y-5 text-[#334155] text-lg leading-relaxed">
            {c.whatIsBody.map((p, i) => <p key={i}>{p}</p>)}
            <ul className="space-y-3 pl-6 list-disc marker:text-[#2563EB]">
              {c.whatIsBuckets.map(([k, v]) => (
                <li key={k}><strong>{k}</strong> — {v}</li>
              ))}
            </ul>
            <p>{c.whatIsClose}</p>
          </div>
        </div>
      </section>

      {/* By category */}
      <section className="hac-container py-16 md:py-20">
        <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-3">
          {c.byCategoryTitle}
        </h2>
        <p className="text-[#64748B] max-w-3xl mb-12">{c.byCategoryLede}</p>
        <div className="space-y-14">
          {c.categoryGroups.map((group) => {
            const color = categoryColor(group.key);
            return (
              <article key={group.key}>
                <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-widest mb-3" style={{ color }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  {group.subtitle}
                </div>
                <h3 className="font-heading text-2xl md:text-4xl text-[#0F172A] leading-tight mb-4">{group.title}</h3>
                <p className="text-[#64748B] leading-relaxed max-w-3xl mb-6">{group.intro}</p>
                <div className="grid md:grid-cols-3 gap-4">
                  {group.picks.map((slug) => {
                    const a = bySlug(slug);
                    if (!a) return null;
                    return (
                      <OgPreviewTooltip key={slug} slug={slug} className="block">
                        <Link to={`/apps/${slug}`}
                          className="block border border-[#E2E8F0] rounded-2xl p-6 hover:border-[#2563EB] hover:shadow-md transition-all bg-white">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="w-10 h-10 flex items-center justify-center text-white font-bold rounded-lg"
                              style={{ backgroundColor: a.color || "#2563EB" }}>{a.name.charAt(0)}</span>
                            <div>
                              <h4 className="font-bold text-[#0F172A]">{a.name}</h4>
                              <div className="text-xs text-[#64748B]">{a.country}</div>
                            </div>
                          </div>
                          <p className="text-sm text-[#64748B] leading-relaxed line-clamp-3">{a.tagline}</p>
                          <div className="flex items-center justify-between mt-4 text-xs text-[#94A3B8]">
                            <span className="inline-flex items-center gap-1">
                              <Star size={12} className="fill-[#2563EB] text-[#2563EB]" />{a.rating?.toFixed(1)}
                            </span>
                            <span className="font-mono">
                              {a.pricing?.free_tier && a.pricing?.monthly_usd === 0 ? c.free : `$${a.pricing?.monthly_usd}${c.monthly}`}
                            </span>
                          </div>
                        </Link>
                      </OgPreviewTooltip>
                    );
                  })}
                </div>
                <Link to={`/categories/${group.key}`} className="inline-flex items-center gap-1 mt-5 text-sm font-medium text-[#2563EB] hover:text-[#1D4ED8]">
                  {c.seeAll} {group.title.toLowerCase()} <ArrowRight size={14} />
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      {/* Personas */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0] py-16 md:py-20">
        <div className="hac-container">
          <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-3">
            {c.personasTitle}
          </h2>
          <p className="text-[#64748B] max-w-3xl mb-12">{c.personasLede}</p>
          <div className="grid md:grid-cols-2 gap-6">
            {c.personas.map((p) => (
              <article key={p.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-8">
                <h3 className="font-heading text-2xl text-[#0F172A] mb-2">{p.name}</h3>
                <p className="text-[#64748B] leading-relaxed mb-5">{p.blurb}</p>
                <div className="space-y-3">
                  {p.picks.map((slug, idx) => (
                    <AppPickRow key={slug} app={bySlug(slug)} index={idx} monthlyLabel={c.monthly} freeLabel={c.free} />
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Equilab alts */}
      <section className="hac-container py-16 md:py-20 max-w-4xl">
        <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-3">
          {c.equilabAltTitle}
        </h2>
        <p className="text-[#64748B] leading-relaxed mb-6">{c.equilabAltLede}</p>
        <div className="space-y-3">
          {c.equilabAlts.map((alt, i) => (
            <AppPickRow key={alt.slug} app={bySlug(alt.slug)} reason={alt.reason} index={i} monthlyLabel={c.monthly} freeLabel={c.free} />
          ))}
        </div>
        <div className="mt-8 p-5 bg-[#EFF6FF] border border-[#DBEAFE] rounded-xl text-sm text-[#1E40AF]">
          <strong className="font-semibold">{c.lang === "nl" ? "Let op:" : "Worth knowing:"}</strong> {c.equilabAltNote}
        </div>
      </section>

      {/* Features explained */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0] py-16 md:py-20">
        <div className="hac-container max-w-5xl">
          <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-3">
            {c.featuresTitle}
          </h2>
          <p className="text-[#64748B] max-w-3xl mb-12">{c.featuresLede}</p>
          <div className="grid md:grid-cols-2 gap-6">
            {c.featuresExplained.map((f) => (
              <article key={f.key} className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
                <h3 className="font-heading text-xl text-[#0F172A] mb-2">{f.title}</h3>
                <p className="text-[#64748B] leading-relaxed text-sm">{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="hac-container py-16 md:py-20 max-w-4xl">
        <div className="overline mb-3 text-[#2563EB]">
          <Target size={12} className="inline mr-2" />
          {c.methodologyEyebrow}
        </div>
        <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-3">
          {c.methodologyTitle}
        </h2>
        <p className="text-[#64748B] leading-relaxed mb-10">{c.methodologyLede}</p>
        <div className="space-y-6">
          {c.methodology.map((m) => (
            <div key={m.title} className="flex gap-5">
              <div className="w-10 h-10 shrink-0 bg-[#EFF6FF] border border-[#DBEAFE] rounded-full flex items-center justify-center text-[#2563EB] font-bold">
                <ShieldCheck size={18} />
              </div>
              <div>
                <h3 className="font-bold text-lg text-[#0F172A] mb-1">{m.title}</h3>
                <p className="text-[#64748B] leading-relaxed">{m.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final recs */}
      <section className="bg-[#0F172A] text-white py-16 md:py-20">
        <div className="hac-container max-w-6xl">
          <div className="overline mb-3 text-[#60A5FA]">
            <Sparkles size={12} className="inline mr-2" />
            {c.finalRecsEyebrow}
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight mb-3">
            {c.finalRecsTitle}
          </h2>
          <p className="text-white/70 max-w-3xl mb-12">{c.finalRecsLede}</p>
          <div className="grid md:grid-cols-2 gap-8">
            {c.finalRecs.map((rec) => (
              <div key={rec.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="font-bold text-xl mb-4">{rec.title}</h3>
                <ol className="space-y-2.5">
                  {rec.picks.map((slug, i) => {
                    const a = bySlug(slug);
                    if (!a) return null;
                    return (
                      <li key={slug}>
                        <OgPreviewTooltip slug={slug} className="block">
                          <Link to={`/apps/${slug}`} className="flex items-center gap-3 text-white/90 hover:text-white">
                            <span className="font-mono text-sm text-[#60A5FA] w-6">{i + 1}.</span>
                            <span className="w-7 h-7 flex items-center justify-center text-white font-bold text-xs rounded-md"
                              style={{ backgroundColor: a.color || "#2563EB" }}>
                              {a.name.charAt(0)}
                            </span>
                            <span className="font-medium">{a.name}</span>
                            <ArrowRight size={14} className="ml-auto opacity-50" />
                          </Link>
                        </OgPreviewTooltip>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="hac-container py-16 md:py-20 max-w-4xl">
        <h2 className="font-heading text-3xl md:text-5xl text-[#0F172A] leading-tight mb-3">
          {c.faqTitle}
        </h2>
        <p className="text-[#64748B] mb-10">{c.faqLede}</p>
        <div className="space-y-3">
          {c.faq.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="hac-container pb-20 md:pb-28">
        <div className="bg-[#EFF6FF] border border-[#DBEAFE] rounded-2xl p-8 md:p-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] leading-tight mb-3">
            {c.ctaTitle}
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mb-6">{c.ctaLede}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/advisor" className="hac-btn-primary">
              <Sparkles size={16} /> {c.ctaPrimary}
            </Link>
            <Link to="/compare" className="hac-btn-secondary">
              {c.ctaSecondary} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListLd) }} />
    </main>
  );
};

export default BestHorseAppsPage;
